import { parsePuppyTopologyRuntimeDocument } from './parsePuppyTopologyRuntimeDocument'
import type {
  PuppyParsedTopologyRuntimeDocument,
  PuppyTopologyRuntimeAssetPayload,
  PuppyTopologyRuntimeDocument,
} from './types'

/**
 * packed topology runtime 魔数：`PUPTPK1\0`。
 */
const PUPPY_TOPOLOGY_RUNTIME_MAGIC = new Uint8Array([0x50, 0x55, 0x50, 0x54, 0x50, 0x4b, 0x31, 0x00])

/**
 * packed topology runtime 内部编码版本。
 */
/** v2 起边记录追加语义拓扑名；读侧兼容 v1，写侧始终产出当前版本。 */
const PUPPY_TOPOLOGY_RUNTIME_BINARY_VERSION = 2

/**
 * 二进制写入器初始容量。
 */
const INITIAL_WRITER_CAPACITY = 1024 * 1024

/**
 * 空可选字符串 ID。
 */
const EMPTY_OPTIONAL_STRING_ID = -1

/**
 * 拓扑 runtime 文本编码器。
 */
const topologyRuntimeTextEncoder = new TextEncoder()

/**
 * 拓扑 runtime 文本解码器。
 */
const topologyRuntimeTextDecoder = new TextDecoder()

/**
 * 二进制写入器。
 */
interface BinaryWriter {
  /** 写入 Uint8。*/
  writeUint8(value: number): void
  /** 写入 Uint32。*/
  writeUint32(value: number): void
  /** 写入 Int32。*/
  writeInt32(value: number): void
  /** 写入 Float32。*/
  writeFloat32(value: number): void
  /** 写入 Float64。*/
  writeFloat64(value: number): void
  /** 写入字节块。*/
  writeBytes(bytes: Uint8Array): void
  /** 导出已写入字节。*/
  toBytes(): Uint8Array
}

/**
 * 二进制读取器。
 */
interface BinaryReader {
  /** 读取 Uint8。*/
  readUint8(): number
  /** 读取 Uint32。*/
  readUint32(): number
  /** 读取 Int32。*/
  readInt32(): number
  /** 读取 Float32。*/
  readFloat32(): number
  /** 读取 Float64。*/
  readFloat64(): number
  /** 读取字节块。*/
  readBytes(length: number): Uint8Array
  /** 剩余字节数。*/
  remaining(): number
}

/**
 * 创建二进制写入器。
 *
 * @returns 二进制写入器。
 */
function createBinaryWriter(): BinaryWriter {
  let buffer = new ArrayBuffer(INITIAL_WRITER_CAPACITY)
  let view = new DataView(buffer)
  let offset = 0

  const ensureCapacity = (additionalLength: number): void => {
    const requiredLength = offset + additionalLength
    if (requiredLength <= buffer.byteLength) {
      return
    }

    let nextLength = buffer.byteLength
    while (nextLength < requiredLength) {
      nextLength *= 2
    }
    const next = new Uint8Array(nextLength)
    next.set(new Uint8Array(buffer, 0, offset), 0)
    buffer = next.buffer
    view = new DataView(buffer)
  }

  return {
    writeUint8(value: number): void {
      ensureCapacity(1)
      view.setUint8(offset, value)
      offset += 1
    },
    writeUint32(value: number): void {
      ensureCapacity(4)
      view.setUint32(offset, value >>> 0, true)
      offset += 4
    },
    writeInt32(value: number): void {
      ensureCapacity(4)
      view.setInt32(offset, value, true)
      offset += 4
    },
    writeFloat32(value: number): void {
      ensureCapacity(4)
      view.setFloat32(offset, Number.isFinite(value) ? value : 0, true)
      offset += 4
    },
    writeFloat64(value: number): void {
      ensureCapacity(8)
      view.setFloat64(offset, Number.isFinite(value) ? value : 0, true)
      offset += 8
    },
    writeBytes(bytes: Uint8Array): void {
      ensureCapacity(bytes.byteLength)
      new Uint8Array(buffer, offset, bytes.byteLength).set(bytes)
      offset += bytes.byteLength
    },
    toBytes(): Uint8Array {
      return new Uint8Array(buffer, 0, offset)
    },
  }
}

/**
 * 创建二进制读取器。
 *
 * @param bytes 源字节。
 * @returns 二进制读取器。
 */
function createBinaryReader(bytes: Uint8Array): BinaryReader {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  let offset = 0

  const assertReadable = (length: number): void => {
    if (offset + length > bytes.byteLength) {
      throw new Error('PuppyCAD topology-runtime.bin 数据被截断')
    }
  }

  return {
    readUint8(): number {
      assertReadable(1)
      const value = view.getUint8(offset)
      offset += 1
      return value
    },
    readUint32(): number {
      assertReadable(4)
      const value = view.getUint32(offset, true)
      offset += 4
      return value
    },
    readInt32(): number {
      assertReadable(4)
      const value = view.getInt32(offset, true)
      offset += 4
      return value
    },
    readFloat32(): number {
      assertReadable(4)
      const value = view.getFloat32(offset, true)
      offset += 4
      return value
    },
    readFloat64(): number {
      assertReadable(8)
      const value = view.getFloat64(offset, true)
      offset += 8
      return value
    },
    readBytes(length: number): Uint8Array {
      assertReadable(length)
      const value = bytes.subarray(offset, offset + length)
      offset += length
      return value
    },
    remaining(): number {
      return bytes.byteLength - offset
    },
  }
}

/**
 * 字符串表构建结果。
 */
interface StringTableBuildResult {
  /** 字符串列表。*/
  strings: string[]
  /** 字符串到 ID 的映射。*/
  idsByString: Map<string, number>
}

/**
 * 将可选字符串加入字符串表。
 *
 * @param input 字符串表输入。
 * @param value 待加入字符串。
 */
function addOptionalString(input: StringTableBuildResult, value: unknown): void {
  if (typeof value !== 'string' || value.length === 0 || input.idsByString.has(value)) {
    return
  }
  input.idsByString.set(value, input.strings.length)
  input.strings.push(value)
}

/**
 * 将字符串数组加入字符串表。
 *
 * @param input 字符串表输入。
 * @param values 字符串数组。
 */
function addStringArray(input: StringTableBuildResult, values: string[] | undefined): void {
  values?.forEach((value) => addOptionalString(input, value))
}

/**
 * 序列化资产元数据。
 *
 * 设计说明：
 * 1. 元数据仍是扩展字段，不能拆成固定二进制字段导致后续格式频繁变更；
 * 2. 写入端与字符串表必须复用同一份序列化结果，避免对象属性顺序差异造成字符串表查找失败。
 *
 * @param asset 拓扑 runtime 资产。
 * @returns 可写入字符串表的元数据 JSON。
 */
function serializeAssetMetadata(asset: PuppyTopologyRuntimeAssetPayload): string | undefined {
  return asset.metadata ? JSON.stringify(asset.metadata) : undefined
}

/**
 * 将资产中的字符串加入字符串表。
 *
 * @param input 字符串表输入。
 * @param asset 拓扑资产。
 */
function addAssetStrings(input: StringTableBuildResult, asset: PuppyTopologyRuntimeAssetPayload): void {
  addOptionalString(input, asset.bodyBindingId)
  addOptionalString(input, asset.geometryObjectId)
  addOptionalString(input, asset.exportDefinitionId)
  addOptionalString(input, asset.bodyId)
  addOptionalString(input, serializeAssetMetadata(asset))

  asset.topologySnapshot.vertices.forEach((vertex) => {
    addOptionalString(input, vertex.subEntityId)
    addOptionalString(input, vertex.ref)
  })
  asset.topologySnapshot.edges.forEach((edge) => {
    addOptionalString(input, edge.subEntityId)
    addOptionalString(input, edge.ref)
    addOptionalString(input, edge.owningFeatureId ?? undefined)
    addOptionalString(input, edge.edgeRole)
    addOptionalString(input, edge.curveKind)
    addOptionalString(input, edge.semanticName)
    addStringArray(input, edge.owningFaceRefs)
  })
  asset.topologySnapshot.faces.forEach((face) => {
    addOptionalString(input, face.subEntityId)
    addOptionalString(input, face.ref)
    addOptionalString(input, face.owningFeatureId ?? undefined)
    addOptionalString(input, face.surfaceKind)
  })
}

/**
 * 构建字符串表。
 *
 * @param document 拓扑 runtime 文档。
 * @returns 字符串表构建结果。
 */
function buildStringTable(document: PuppyTopologyRuntimeDocument): StringTableBuildResult {
  const result: StringTableBuildResult = {
    strings: [],
    idsByString: new Map(),
  }
  document.assets.forEach((asset) => addAssetStrings(result, asset))
  return result
}

/**
 * 读取必需字符串 ID。
 *
 * @param table 字符串表。
 * @param value 字符串值。
 * @returns 字符串 ID。
 */
function resolveRequiredStringId(table: StringTableBuildResult, value: string): number {
  const id = table.idsByString.get(value)
  if (id === undefined) {
    throw new Error(`PuppyCAD topology-runtime 字符串表缺少字段: ${value}`)
  }
  return id
}

/**
 * 读取可选字符串 ID。
 *
 * @param table 字符串表。
 * @param value 字符串值。
 * @returns 字符串 ID。
 */
function resolveOptionalStringId(table: StringTableBuildResult, value: string | null | undefined): number {
  return value && value.length > 0
    ? resolveRequiredStringId(table, value)
    : EMPTY_OPTIONAL_STRING_ID
}

/**
 * 写入字符串表。
 *
 * @param writer 二进制写入器。
 * @param strings 字符串列表。
 */
function writeStringTable(writer: BinaryWriter, strings: string[]): void {
  writer.writeUint32(strings.length)
  strings.forEach((value) => {
    const bytes = topologyRuntimeTextEncoder.encode(value)
    writer.writeUint32(bytes.byteLength)
    writer.writeBytes(bytes)
  })
}

/**
 * 读取字符串表。
 *
 * @param reader 二进制读取器。
 * @returns 字符串列表。
 */
function readStringTable(reader: BinaryReader): string[] {
  const count = reader.readUint32()
  const strings: string[] = []
  for (let index = 0; index < count; index += 1) {
    const length = reader.readUint32()
    strings.push(topologyRuntimeTextDecoder.decode(reader.readBytes(length)))
  }
  return strings
}

/**
 * 写入三维点。
 *
 * @param writer 二进制写入器。
 * @param point 三维点。
 */
function writePoint3D(writer: BinaryWriter, point: TopologyPoint3D): void {
  writer.writeFloat32(point[0] ?? 0)
  writer.writeFloat32(point[1] ?? 0)
  writer.writeFloat32(point[2] ?? 0)
}

/**
 * 读取三维点。
 *
 * @param reader 二进制读取器。
 * @returns 三维点。
 */
function readPoint3D(reader: BinaryReader): TopologyPoint3D {
  return [reader.readFloat32(), reader.readFloat32(), reader.readFloat32()]
}

/**
 * 写入 Uint32 数组。
 *
 * @param writer 二进制写入器。
 * @param values 数组值。
 */
function writeUint32Array(writer: BinaryWriter, values: number[] | undefined): void {
  writer.writeUint32(values?.length ?? 0)
  values?.forEach((value) => writer.writeUint32(value))
}

/**
 * 读取 Uint32 数组。
 *
 * @param reader 二进制读取器。
 * @returns 数组值。
 */
function readUint32Array(reader: BinaryReader): number[] {
  const count = reader.readUint32()
  const values: number[] = []
  for (let index = 0; index < count; index += 1) {
    values.push(reader.readUint32())
  }
  return values
}

/**
 * 写入字符串 ID 数组。
 *
 * @param writer 二进制写入器。
 * @param table 字符串表。
 * @param values 字符串数组。
 */
function writeStringIdArray(writer: BinaryWriter, table: StringTableBuildResult, values: string[] | undefined): void {
  writer.writeUint32(values?.length ?? 0)
  values?.forEach((value) => writer.writeUint32(resolveRequiredStringId(table, value)))
}

/**
 * 读取字符串 ID 数组。
 *
 * @param reader 二进制读取器。
 * @param strings 字符串表。
 * @returns 字符串数组。
 */
function readStringIdArray(reader: BinaryReader, strings: string[]): string[] {
  const count = reader.readUint32()
  const values: string[] = []
  for (let index = 0; index < count; index += 1) {
    values.push(readRequiredString(strings, reader.readUint32()))
  }
  return values
}

/**
 * 写入可选数字。
 *
 * @param writer 二进制写入器。
 * @param value 数字值。
 */
function writeOptionalNumber(writer: BinaryWriter, value: number | undefined): void {
  writer.writeUint8(typeof value === 'number' && Number.isFinite(value) ? 1 : 0)
  if (typeof value === 'number' && Number.isFinite(value)) {
    writer.writeFloat64(value)
  }
}

/**
 * 读取可选数字。
 *
 * @param reader 二进制读取器。
 * @returns 数字值。
 */
function readOptionalNumber(reader: BinaryReader): number | undefined {
  return reader.readUint8() === 1 ? reader.readFloat64() : undefined
}

/**
 * 写入拓扑顶点。
 *
 * @param writer 二进制写入器。
 * @param table 字符串表。
 * @param vertex 顶点快照。
 */
function writeVertex(writer: BinaryWriter, table: StringTableBuildResult, vertex: TopologySnapshotVertex): void {
  writer.writeUint32(vertex.index)
  writer.writeUint8(vertex.role === 'curve-reference' ? 1 : 0)
  writer.writeUint32(resolveRequiredStringId(table, vertex.subEntityId))
  writer.writeUint32(resolveRequiredStringId(table, vertex.ref))
  writePoint3D(writer, vertex.position)
}

/**
 * 读取拓扑顶点。
 *
 * @param reader 二进制读取器。
 * @param strings 字符串表。
 * @returns 顶点快照。
 */
function readVertex(reader: BinaryReader, strings: string[]): TopologySnapshotVertex {
  return {
    index: reader.readUint32(),
    role: reader.readUint8() === 1 ? 'curve-reference' : 'topological',
    subEntityId: readRequiredString(strings, reader.readUint32()),
    ref: readRequiredString(strings, reader.readUint32()),
    position: readPoint3D(reader),
  }
}

/**
 * 写入拓扑边。
 *
 * @param writer 二进制写入器。
 * @param table 字符串表。
 * @param edge 边快照。
 */
function writeEdge(writer: BinaryWriter, table: StringTableBuildResult, edge: TopologySnapshotEdge): void {
  writer.writeUint32(edge.index)
  writer.writeUint32(resolveRequiredStringId(table, edge.subEntityId))
  writer.writeUint32(resolveRequiredStringId(table, edge.ref))
  writeOptionalNumber(writer, edge.shapeHash)
  writer.writeInt32(resolveOptionalStringId(table, edge.owningFeatureId ?? undefined))
  writer.writeUint32(edge.vertexAIndex)
  writer.writeUint32(edge.vertexBIndex)
  writer.writeInt32(resolveOptionalStringId(table, edge.edgeRole))
  writer.writeInt32(resolveOptionalStringId(table, edge.curveKind))
  // v2 起新增：语义拓扑名，缺省写入 -1，读侧按版本判断是否存在该字段。
  writer.writeInt32(resolveOptionalStringId(table, edge.semanticName))
  writeStringIdArray(writer, table, edge.owningFaceRefs)
  writeUint32Array(writer, edge.adjacentFaceIndices)
  writer.writeUint32(edge.sampledPoints?.length ?? 0)
  edge.sampledPoints?.forEach((point) => writePoint3D(writer, point))
}

/**
 * 读取拓扑边。
 *
 * @param reader 二进制读取器。
 * @param strings 字符串表。
 * @returns 边快照。
 */
function readEdge(reader: BinaryReader, strings: string[], binaryVersion: number): TopologySnapshotEdge {
  const edge: TopologySnapshotEdge = {
    index: reader.readUint32(),
    subEntityId: readRequiredString(strings, reader.readUint32()),
    ref: readRequiredString(strings, reader.readUint32()),
    shapeHash: readOptionalNumber(reader),
    owningFeatureId: readOptionalString(strings, reader.readInt32()) ?? null,
    vertexAIndex: reader.readUint32(),
    vertexBIndex: reader.readUint32(),
    edgeRole: readOptionalString(strings, reader.readInt32()),
    curveKind: readOptionalString(strings, reader.readInt32()) as TopologyCurveKind | undefined,
    // v1 包没有该字段，跳过读取即可；缺语义名的边由消费方退回坐标兼容路径。
    semanticName: binaryVersion >= 2 ? readOptionalString(strings, reader.readInt32()) : undefined,
    owningFaceRefs: readStringIdArray(reader, strings),
    adjacentFaceIndices: readUint32Array(reader),
  }
  const sampledPointCount = reader.readUint32()
  if (sampledPointCount > 0) {
    edge.sampledPoints = []
    for (let index = 0; index < sampledPointCount; index += 1) {
      edge.sampledPoints.push(readPoint3D(reader))
    }
  }
  return edge
}

/**
 * 写入拓扑面。
 *
 * @param writer 二进制写入器。
 * @param table 字符串表。
 * @param face 面快照。
 */
function writeFace(writer: BinaryWriter, table: StringTableBuildResult, face: TopologySnapshotFace): void {
  writer.writeUint32(face.index)
  writer.writeUint32(resolveRequiredStringId(table, face.subEntityId))
  writer.writeUint32(resolveRequiredStringId(table, face.ref))
  writeOptionalNumber(writer, face.shapeHash)
  writer.writeInt32(resolveOptionalStringId(table, face.owningFeatureId ?? undefined))
  writer.writeUint32(face.triangleRange[0])
  writer.writeUint32(face.triangleRange[1])
  writePoint3D(writer, face.localNormal)
  writePoint3D(writer, face.localPoint)
  writer.writeInt32(resolveOptionalStringId(table, face.surfaceKind))
  writeUint32Array(writer, face.adjacentEdgeIndices)
  writeUint32Array(writer, face.adjacentFaceIndices)
}

/**
 * 读取拓扑面。
 *
 * @param reader 二进制读取器。
 * @param strings 字符串表。
 * @returns 面快照。
 */
function readFace(reader: BinaryReader, strings: string[]): TopologySnapshotFace {
  return {
    index: reader.readUint32(),
    subEntityId: readRequiredString(strings, reader.readUint32()),
    ref: readRequiredString(strings, reader.readUint32()),
    shapeHash: readOptionalNumber(reader),
    owningFeatureId: readOptionalString(strings, reader.readInt32()) ?? null,
    triangleRange: [reader.readUint32(), reader.readUint32()],
    localNormal: readPoint3D(reader),
    localPoint: readPoint3D(reader),
    surfaceKind: readOptionalString(strings, reader.readInt32()) as TopologySurfaceKind | undefined,
    adjacentEdgeIndices: readUint32Array(reader),
    adjacentFaceIndices: readUint32Array(reader),
  }
}

/**
 * 写入 faceTriangleRanges。
 *
 * @param writer 二进制写入器。
 * @param ranges 面三角范围。
 */
function writeFaceTriangleRanges(writer: BinaryWriter, ranges: [number, number][] | undefined): void {
  writer.writeUint8(ranges && ranges.length > 0 ? 1 : 0)
  writer.writeUint32(ranges?.length ?? 0)
  ranges?.forEach((range) => {
    writer.writeUint32(range[0])
    writer.writeUint32(range[1])
  })
}

/**
 * 读取 faceTriangleRanges。
 *
 * @param reader 二进制读取器。
 * @returns 面三角范围。
 */
function readFaceTriangleRanges(reader: BinaryReader): [number, number][] | undefined {
  const hasRanges = reader.readUint8() === 1
  const count = reader.readUint32()
  const ranges: [number, number][] = []
  for (let index = 0; index < count; index += 1) {
    ranges.push([reader.readUint32(), reader.readUint32()])
  }
  return hasRanges ? ranges : undefined
}

/**
 * 写入单个拓扑资产。
 *
 * @param writer 二进制写入器。
 * @param table 字符串表。
 * @param asset 拓扑资产。
 */
function writeAsset(writer: BinaryWriter, table: StringTableBuildResult, asset: PuppyTopologyRuntimeAssetPayload): void {
  writer.writeUint32(resolveRequiredStringId(table, asset.bodyBindingId))
  writer.writeUint32(resolveRequiredStringId(table, asset.geometryObjectId))
  writer.writeInt32(resolveOptionalStringId(table, asset.exportDefinitionId))
  writer.writeInt32(resolveOptionalStringId(table, asset.bodyId))
  writer.writeInt32(resolveOptionalStringId(table, serializeAssetMetadata(asset)))
  writeFaceTriangleRanges(writer, asset.faceTriangleRanges)

  writer.writeUint32(asset.topologySnapshot.vertices.length)
  asset.topologySnapshot.vertices.forEach((vertex) => writeVertex(writer, table, vertex))
  writer.writeUint32(asset.topologySnapshot.edges.length)
  asset.topologySnapshot.edges.forEach((edge) => writeEdge(writer, table, edge))
  writer.writeUint32(asset.topologySnapshot.faces.length)
  asset.topologySnapshot.faces.forEach((face) => writeFace(writer, table, face))
}

/**
 * 读取单个拓扑资产。
 *
 * @param reader 二进制读取器。
 * @param strings 字符串表。
 * @param binaryVersion packed 二进制格式版本。
 * @returns 拓扑资产。
 */
function readAsset(
  reader: BinaryReader,
  strings: string[],
  binaryVersion: number,
): PuppyTopologyRuntimeAssetPayload {
  const bodyBindingId = readRequiredString(strings, reader.readUint32())
  const geometryObjectId = readRequiredString(strings, reader.readUint32())
  const exportDefinitionId = readOptionalString(strings, reader.readInt32())
  const bodyId = readOptionalString(strings, reader.readInt32())
  const metadataJson = readOptionalString(strings, reader.readInt32())
  const faceTriangleRanges = readFaceTriangleRanges(reader)
  const vertices = readRepeated(reader, () => readVertex(reader, strings))
  const edges = readRepeated(reader, () => readEdge(reader, strings, binaryVersion))
  const faces = readRepeated(reader, () => readFace(reader, strings))

  return {
    bodyBindingId,
    geometryObjectId,
    ...(exportDefinitionId ? { exportDefinitionId } : {}),
    ...(bodyId ? { bodyId } : {}),
    ...(faceTriangleRanges ? { faceTriangleRanges } : {}),
    ...(metadataJson ? { metadata: JSON.parse(metadataJson) as Record<string, unknown> } : {}),
    topologySnapshot: {
      vertices,
      edges,
      faces,
    },
  }
}

/**
 * 读取重复记录。
 *
 * @param reader 二进制读取器。
 * @param readItem 单项读取函数。
 * @returns 记录数组。
 */
function readRepeated<T>(reader: BinaryReader, readItem: () => T): T[] {
  const count = reader.readUint32()
  const values: T[] = []
  for (let index = 0; index < count; index += 1) {
    values.push(readItem())
  }
  return values
}

/**
 * 从字符串表读取必需字符串。
 *
 * @param strings 字符串表。
 * @param id 字符串 ID。
 * @returns 字符串。
 */
function readRequiredString(strings: string[], id: number): string {
  const value = strings[id]
  if (typeof value !== 'string') {
    throw new Error(`PuppyCAD topology-runtime.bin 字符串 ID 无效: ${id}`)
  }
  return value
}

/**
 * 从字符串表读取可选字符串。
 *
 * @param strings 字符串表。
 * @param id 字符串 ID。
 * @returns 字符串。
 */
function readOptionalString(strings: string[], id: number): string | undefined {
  return id === EMPTY_OPTIONAL_STRING_ID ? undefined : readRequiredString(strings, id)
}

/**
 * 判断二进制数据是否匹配拓扑 runtime 魔数。
 *
 * @param bytes 待校验字节。
 * @returns 是否匹配魔数。
 */
function hasExpectedMagic(bytes: Uint8Array): boolean {
  if (bytes.byteLength < PUPPY_TOPOLOGY_RUNTIME_MAGIC.byteLength) {
    return false
  }
  for (let index = 0; index < PUPPY_TOPOLOGY_RUNTIME_MAGIC.byteLength; index += 1) {
    if (bytes[index] !== PUPPY_TOPOLOGY_RUNTIME_MAGIC[index]) {
      return false
    }
  }
  return true
}

/**
 * 将拓扑 runtime 文档编码为 `.puppy` packed 二进制文件。
 *
 * @param document 待编码拓扑 runtime 文档。
 * @returns 二进制拓扑 runtime 字节。
 */
export function encodePuppyTopologyRuntimeDocument(document: PuppyTopologyRuntimeDocument): Uint8Array {
  const table = buildStringTable(document)
  const writer = createBinaryWriter()
  writer.writeBytes(PUPPY_TOPOLOGY_RUNTIME_MAGIC)
  writer.writeUint32(PUPPY_TOPOLOGY_RUNTIME_BINARY_VERSION)
  writer.writeUint32(document.version)
  writeStringTable(writer, table.strings)
  writer.writeUint32(document.assets.length)
  document.assets.forEach((asset) => writeAsset(writer, table, asset))
  return writer.toBytes()
}

/**
 * 从 `.puppy` packed 二进制文件解码拓扑 runtime 文档。
 *
 * @param bytes 二进制拓扑 runtime 字节。
 * @returns 解析后的拓扑 runtime 文档。
 * @throws 当输入字节不含有效的 topology-runtime 魔数时。
 * @throws 当二进制版本、编码结构、尾部字节或 runtime 文档校验失败时，抛出包含解析上下文的错误。
 */
export function decodePuppyTopologyRuntimeDocument(bytes: Uint8Array): PuppyParsedTopologyRuntimeDocument {
  if (!hasExpectedMagic(bytes)) {
    throw new Error('PuppyCAD topology-runtime.bin 魔数无效')
  }

  try {
    const reader = createBinaryReader(bytes.subarray(PUPPY_TOPOLOGY_RUNTIME_MAGIC.byteLength))
    const binaryVersion = reader.readUint32()
    if (binaryVersion < 1 || binaryVersion > PUPPY_TOPOLOGY_RUNTIME_BINARY_VERSION) {
      throw new Error(`不支持的 packed topology-runtime 版本: ${binaryVersion}`)
    }

    const documentVersion = reader.readUint32()
    const strings = readStringTable(reader)
    const assets = readRepeated(reader, () => readAsset(reader, strings, binaryVersion))
    if (reader.remaining() !== 0) {
      throw new Error(`拓扑 runtime 存在未消费尾部字节: ${reader.remaining()}`)
    }

    return parsePuppyTopologyRuntimeDocument({
      version: documentVersion,
      assets,
    })
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    throw new Error(`PuppyCAD topology-runtime.bin 解析失败: ${message}`)
  }
}
